import _ from 'lodash';
import { SemanticUI } from '@iterable/deprecated_react_components';
import { createSelector } from 'reselect';
import { displayDate, DATE_FORMAT_FULL_WITH_TIME } from 'utils/date';
import { selectDefinedFieldTypes } from 'modules/catalogFieldTypes/selectors';
import { FIELD_TYPES } from 'modules/catalogFieldTypes/constants';
import JSONTree from 'core/jsonTree';
import { selectIsBossOrSalesBossLevel, selectIsCatalogShowSizeFlagOn } from 'core/userContext';
import { ByteUnit } from 'utils/l10n';
import { localizeFileSize } from 'utils/i18nHelpers';

export const getCatalogDataList = (state) => state.catalogs.tables.dataList;
export const getCatalogDataObj = (state) => state.catalogs.tables.dataObj;
export const getCatalogName = (state, catalogName) =>
  catalogName || state.catalogs.tables.tableName;
export const getCatalogIsLoading = (state) => state.catalogs.tables.isLoading;
export const getCatalogIsSubmitting = (state) => state.catalogs.tables.isSubmittingForm;
export const getCatalogPending = (state) => state.catalogs.tables.pending;
export const getCatalogPendingDelete = (state) => state.catalogs.tables.pendingDelete;
export const getCatalogFilters = (state) => state.catalogs.tables.filters;
export const getCatalogTables = (state) => state.catalogs.tables;
export const getCatalogIsDisplayTable = (state) => state.catalogs.tables.isDisplayTable;

export const selectCatalogItemsTotal = createSelector([getCatalogTables], (tables) =>
  _.get(tables, 'total', 0),
);

export const selectCatalogSize = createSelector([getCatalogTables], (tables) =>
  _.get(tables, 'sizeInBytes', -1),
);

export const placeHolderPendingColumns = {
  catalogValue: ' ',
  size: ' ',
  lastModified: ' ',
};

export const displayCellContentWithIcon = (display, name, isExpanded) => {
  const icon = `file${isExpanded ? ' ' : ' alternate '}outline`;
  switch (display) {
    case 'pending delete':
      return (
        <div className="tableCellNameLabel tableCellNameLabelDelete">
          <SemanticUI.Icon name="hourglass half" color="grey" />
          <div className="cellName">
            <span className="cellNameLabel">{name}</span> <span>(pending delete)</span>
          </div>
        </div>
      );
    case 'pending':
      return (
        <div className="tableCellNameLabel">
          <SemanticUI.Icon name="hourglass half" />
          <div className="cellName">{name} (pending)</div>
        </div>
      );
    default:
      return (
        <div className="tableCellNameLabel">
          <SemanticUI.Icon name={icon} />
          <div className="cellName">{name}</div>
        </div>
      );
  }
};

// because each document (item) could have different fields, we need to iterate
// through every document and gather all the field names
// this could be simplified with a separate API call to retrieve field names
const selectCatalogFieldNames = createSelector(
  [getCatalogName, getCatalogDataObj],
  (tableName, dataObj) => {
    const headerObjs = _.keys(_.get(dataObj, tableName)).map((item) =>
      _.get(dataObj, [tableName, item, 'value']),
    );
    return _.union(
      _.flatten(headerObjs.map((d) => (typeof d === 'string' ? _.keys(JSON.parse(d)) : _.keys(d)))),
    );
  },
);

export const getRJVView = (src, key) => (
  <JSONTree
    key={key}
    rootName={null}
    enableClipboard={false}
    hideItemCount={true}
    displayDataTypes={false}
    src={src}
  />
);

const getKeysForHeader = ({ headerKeys, definedFields }) =>
  headerKeys.map((k) => ({
    name: k,
    value: k,
    minWidth: 200,
    sortable:
      k in definedFields &&
      definedFields[k] !== FIELD_TYPES.geo_location &&
      definedFields[k] !== FIELD_TYPES.object,
  }));

// this is used for "spreadsheet"/"table" view
const getCatalogTableHeaderWithAllValues = ({ headerKeys, definedFields }) => [
  {
    name: 'Key',
    value: 'col1',
    type: 'text',
    hideToggle: true,
    minWidth: 200,
  },
  ...getKeysForHeader({ headerKeys, definedFields }),
  {
    name: 'Size',
    value: 'size',
    width: 120,
  },
  {
    name: 'Last Modified',
    value: 'lastModified',
    hideToggle: true,
  },
];

export const selectCatalogTableHeader = createSelector(
  [getCatalogName, getCatalogIsDisplayTable, selectCatalogFieldNames, selectDefinedFieldTypes],
  (tableName, isDisplayTable, headerKeys, definedFields) => {
    // if display as table not JSON (Tabular)
    if (isDisplayTable && tableName) {
      return getCatalogTableHeaderWithAllValues({ tableName, headerKeys, definedFields });
    }
    // Columns for JSON View catalog table.
    return [
      {
        name: 'Key',
        value: 'col1',
        link: true,
      },
      {
        name: 'Value',
        value: 'catalogValue',
      },
      {
        name: 'Size',
        value: 'size',
        width: 120,
      },
      {
        name: 'Last Modified',
        value: 'lastModified',
        width: 200,
      },
    ];
  },
);

const getStringifiedObject = (v) =>
  typeof v === 'object' || typeof v === 'boolean' ? JSON.stringify(v) : v;

const getParsedValue = (val) => (typeof val === 'string' ? _.attempt(() => JSON.parse(val)) : val);

export const getValueObject = ({ dataObj, tableName, name, headerKeys, definedFields }) => {
  const val = _.get(dataObj, [tableName, name, 'value']);
  return _.attempt(() =>
    _.zipObject(
      headerKeys,
      headerKeys.map((k, i) => {
        const src = getStringifiedObject(_.get(getParsedValue(val), k));
        const fieldType = definedFields[k];
        // display data in jsonTree if it is an object/geo_location type
        return fieldType === FIELD_TYPES.object || fieldType === FIELD_TYPES.geo_location
          ? getRJVView(src, `${k}-${i}`)
          : src;
      }),
    ),
  );
};

// massaging the key row object into a format tableBasic can use
// if the key is in pendingDelete, only show "pending delete"
export const createRowObjectForKeys = ({ dataObj, tableName, name, pendingDelete }) =>
  name in _.get(pendingDelete, tableName, {})
    ? {
        col1: displayCellContentWithIcon('pending delete', name),
        ...placeHolderPendingColumns,
      }
    : {
        ..._.get(dataObj, [tableName, name], {}),
        actions: 'delete',
        col1: displayCellContentWithIcon('key', _.get(dataObj, [tableName, name, 'key'], '')),
        catalogValue: getRJVView(
          getStringifiedObject(_.get(dataObj, [tableName, name, 'value'], '{}')),
          name,
        ),
        size: `${_.get(dataObj, [tableName, name, 'size'], '')} bytes`,
        lastModified: _.get(dataObj, [tableName, name, 'lastModified'])
          ? displayDate(
              _.get(dataObj, [tableName, name, 'lastModified'], ''),
              DATE_FORMAT_FULL_WITH_TIME,
            )
          : '',
      };

// massaging the key row object into a format tableBasic can use (in "spreadsheet"/"table" mode)
// if the key is in pendingDelete, only show "pending delete"
const createRowObjectForKeysWithValues = ({
  dataObj,
  tableName,
  name,
  headerKeys,
  pendingDelete,
  definedFields,
}) =>
  name in _.get(pendingDelete, tableName, {})
    ? {
        col1: displayCellContentWithIcon('pending delete', name),
        ...placeHolderPendingColumns,
      }
    : {
        ..._.get(dataObj, [tableName, name], {}),
        actions: 'delete',
        col1: displayCellContentWithIcon('key', _.get(dataObj, [tableName, name, 'key'], '')),
        size: `${_.get(dataObj, [tableName, name, 'size'], '')} bytes`,
        lastModified: _.get(dataObj, [tableName, name, 'lastModified'])
          ? displayDate(
              _.get(dataObj, [tableName, name, 'lastModified'], ''),
              DATE_FORMAT_FULL_WITH_TIME,
            )
          : '',
        ...getValueObject({ dataObj, tableName, name, headerKeys, definedFields }),
      };

// massaging the table row object into a format tableBasic can use
// if the table is in pendingDelete, only show "pending delete"
const createRowObjectForTables = ({ dataObj, name, pendingDelete }) =>
  name in _.get(pendingDelete, 'tables', {})
    ? {
        col1: displayCellContentWithIcon('pending delete', name),
        ...placeHolderPendingColumns,
      }
    : {
        ...placeHolderPendingColumns,
        ...dataObj[name],
        col1: name,
        link: `/catalogs/table/${name}`,
      };

const getPending = ({ tableName, pending }) => {
  if (tableName) {
    return pending[tableName]
      ? pending[tableName].map((p) => ({
          col1: displayCellContentWithIcon('pending', p.name),
          ...placeHolderPendingColumns,
        }))
      : [];
  }
  return pending.tables
    ? pending.tables.map((p) => ({
        col1: displayCellContentWithIcon('pending', p.name),
        ...placeHolderPendingColumns,
      }))
    : [];
};

// this is used for "spreadsheet"/"table" view
export const getCatalogTableRowsWithAllValues = ({
  dataList,
  dataObj,
  tableName,
  pending,
  pendingDelete,
  headerKeys,
  definedFields,
}) => [
  ...getPending({ tableName, pending }),
  ..._.uniqBy(dataList, 'name').map((d) => {
    const { name } = d;
    // keys and values for specific table
    return createRowObjectForKeysWithValues({
      dataObj,
      tableName,
      name,
      headerKeys,
      pendingDelete,
      definedFields,
    });
  }),
];

export const selectAllCatalogsTableRows = createSelector(
  [getCatalogDataList, getCatalogDataObj, getCatalogPending, getCatalogPendingDelete],
  (dataList, dataObj, pending, pendingDelete) => [
    ...getPending({ pending }),
    ...dataList.map((d) => {
      const { name } = d;
      return createRowObjectForTables({ dataObj, name, pendingDelete });
    }),
  ],
);

export const selectCatalogTableRows = createSelector(
  [
    getCatalogDataList,
    getCatalogDataObj,
    getCatalogName,
    getCatalogPending,
    getCatalogPendingDelete,
    getCatalogIsDisplayTable,
    selectCatalogFieldNames,
    selectDefinedFieldTypes,
  ],
  (
    dataList,
    dataObj,
    tableName,
    pending,
    pendingDelete,
    isDisplayTable,
    headerKeys,
    definedFields,
  ) => {
    if (isDisplayTable && tableName) {
      return getCatalogTableRowsWithAllValues({
        dataList,
        dataObj,
        tableName,
        pending,
        pendingDelete,
        headerKeys,
        definedFields,
      });
    }
    return [
      ...getPending({ tableName, pending }),
      ...dataList.map((d) => {
        const { name } = d;
        return createRowObjectForKeys({
          dataObj,
          tableName,
          name,
          pendingDelete,
        });
      }),
    ];
  },
);

export const selectCatalogIsLoading = createSelector(
  [getCatalogIsLoading],
  (isLoading) => isLoading,
);

export const selectCatalogIsDisplayTable = createSelector(
  [getCatalogIsDisplayTable],
  (isTable) => isTable,
);

export const selectCatalogIsSubmitting = createSelector(
  [getCatalogIsSubmitting],
  (isSubmitting) => isSubmitting,
);

export const selectCatalogNames = createSelector([getCatalogDataObj], (dataObj) =>
  _.keys(dataObj).map((d) => ({ value: d, text: d })),
);

/**
 * selectCatalogsAsDropdownOptions
 * used to display selectable dropdown options for the catalogs in the
 * modules/components/createCollectionModal component
 */
export const selectCatalogsAsDropdownOptions = createSelector([getCatalogDataObj], (dataObj) => {
  if (!dataObj) {
    return [];
  }

  return Object.keys(dataObj).map((catalogName) => ({
    label: catalogName,
    value: catalogName,
  }));
});

export const selectCatalogNamesOnlyCaseInsensitive = createSelector(
  [getCatalogDataObj],
  (dataObj) => _.keys(dataObj).map((d) => d.toLowerCase()),
);

export const selectCatalogTableFilters = createSelector([getCatalogFilters], (filters) => filters);

const selectCatalogCreatedAt = createSelector(
  [getCatalogDataObj, getCatalogName],
  (dataObj, catalogName) => {
    const formattedDate = _.attempt(() =>
      displayDate(_.get(dataObj, [catalogName, 'createdAt']), DATE_FORMAT_FULL_WITH_TIME),
    );
    return _.isError(formattedDate) || formattedDate === 'Invalid date' ? '-' : formattedDate;
  },
);

export const selectCatalogMetadataItems = createSelector(
  [
    selectCatalogCreatedAt,
    selectCatalogItemsTotal,
    selectIsCatalogShowSizeFlagOn,
    selectIsBossOrSalesBossLevel,
    selectCatalogSize,
  ],
  (createdAt, total, isSizeShown, isBoss, sizeInBytes) =>
    [
      { label: 'Items in catalog', text: total },
      (isBoss || isSizeShown) && sizeInBytes >= 0
        ? { label: 'Catalog size', text: localizeFileSize(sizeInBytes, ByteUnit.AUTO).formatted }
        : null,
      {
        label: 'Created on',
        text: createdAt,
      },
    ].filter((v) => v),
);
